// System
import React, { useEffect, useMemo } from 'react';

// Components
import { TablePagination } from '@mui/material';
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';

//  Table
import { useRubberDuckRoundsTableColumns } from './useRubberDuckRoundsTableColumns';

// Redux
import { useDispatch } from 'react-redux';
import { RubberDuckRoundDto } from '@Modal/reducers/rounds/types';
import { useRubberDuckRoundsTable } from '@Modal/hooks/rounds/useRubberDuckRoundsTable';

// Graphql
import { usePaginatedRubberDuckRoundsQuery } from '@Src/generated.graphql';

// Hooks
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useRubberDuckRoundDrawer } from '@Modal/hooks/rounds/useRubberDuckRoundDrawer';

export const RubberDuckRoundsTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    roundsListFilters,
    columnVisibility,
    visiblityCustomizable,
    limit,
    total,
    page,
    setLimit,
    setPage,
    setTotal,
    roundTableFilters,
  } = useRubberDuckRoundsTable();
  const columns = useRubberDuckRoundsTableColumns();
  const { handleRoundRowClick } = useRubberDuckRoundDrawer();
  const { userPermissions, currentUser } = useAuthorization();
  const permissionToViewRounds =
    userPermissions?.includes('CAN_VIEW_RUBBER_DUCK_ROUNDS') || currentUser?.isSuper;

  const { data, loading, refetch } = usePaginatedRubberDuckRoundsQuery({
    variables: {
      limit,
      page,
      filter: roundTableFilters,
    },
    skip: !permissionToViewRounds,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, roundsListFilters),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (data?.paginatedRubberDuckRounds) {
      const { total, limit, page } = data.paginatedRubberDuckRounds;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!permissionToViewRounds) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  return (
    <>
      <TableContainerEx sx={{ height: '70vh' }} pageNumber={1}>
        <TableEx name="RubberDuckRoundsList">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.paginatedRubberDuckRounds?.docs.map((row: RubberDuckRoundDto) => (
              <TableRowPresenter
                id={row._id}
                key={row._id}
                columns={visibleColumns}
                row={row}
                onClick={() => handleRoundRowClick(row.roundId)}
              />
            ))}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
      <TableEx name="CustomerListFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableEx>
    </>
  );
};
